import { useEffect, useRef, useState } from 'react';

import cn from 'classnames';
import patternValidators, { normalizeEmailList } from 'helpers/validators/pattern-validators';
import { useQueryParams } from 'hooks/query-params';
import { usePartnerV1UploadActions } from 'hooks/upload-actions';
import { useNavigate, useParams } from 'react-router-dom';
import useLocalStorage from 'react-use-localstorage';

import { ChecksSelector } from 'components/ChecksSelector/ChecksSelector';
import { Button, Input, InputGroup, Select, ToggleSwitchInput } from 'components/FormComponents';
import { Loading } from 'components/Loading/Loading';

import {
  useAddCandidateChecksMutation,
  useGetBrandsQuery,
  useGetBundlesQuery,
  useGetCandidateQuery,
  useGetCompanyChecksQuery,
  useGetCompanyQuery,
  useGetFormsQuery,
  useGetTeamsQuery,
  useGetUsersQuery,
  useLazyGetBrandUploadsQuery,
  useStartPartnerAssessmentMutation,
  useUpdateCandidateMutation,
} from 'api/partner-v1-api';

import styles from './AssessmentDetails.module.scss';

export const AssessmentDetails = () => {
  const { partner, token } = useParams();
  const [checkType, partnerApplicationId] = useQueryParams(['type', 'partner_application_id']);

  const navigate = useNavigate();

  const { data: candidate, error: candidateFetchError } = useGetCandidateQuery(
    { token, applicationId: partnerApplicationId },
    { skip: !partnerApplicationId },
  );

  const { data: brands } = useGetBrandsQuery(token, { skip: !partnerApplicationId });
  const { data: teams } = useGetTeamsQuery(token, { skip: !partnerApplicationId });
  const { data: forms, isLoading: loadingForms } = useGetFormsQuery(token, {
    skip: !partnerApplicationId,
  });
  const { data: company } = useGetCompanyQuery(token, { skip: !partnerApplicationId });
  const { data: companyChecks, isLoading: loadingCompanyChecks } = useGetCompanyChecksQuery(token, {
    skip: !partnerApplicationId,
  });
  const { data: users } = useGetUsersQuery(token, { skip: !partnerApplicationId });
  const { data: bundles } = useGetBundlesQuery(token, { skip: !partnerApplicationId });

  const { getUpload, signUpload, createUpload, deleteUpload } = usePartnerV1UploadActions(token);

  const [getBrandUploads] = useLazyGetBrandUploadsQuery();
  const [updateCandidate] = useUpdateCandidateMutation();
  const [addCandidateChecks] = useAddCandidateChecksMutation();
  const [startPartnerAssessment] = useStartPartnerAssessmentMutation();

  const selectorRef = useRef(null);

  const [loading, setLoading] = useState(false);

  const [check, setCheck] = useState();

  const [valid, setValidity] = useState(false);

  const [ccNotificationEmailsEnabled, setCcNotificationEmailsEnabled] = useLocalStorage(
    'ccNotificationEmailsEnabled',
    'false',
  );
  const [ccNotificationEmails, setCcNotificationEmails] = useLocalStorage(
    'ccNotificationEmails',
    '',
  );
  const [userId, setUserId] = useState(candidate?.user_id);
  const [teamId, setTeamId] = useState(candidate?.team_id);

  // Redirect if candidate exists
  useEffect(() => {
    if (candidate?.partner_application_started)
      navigate(`/assessments/progress/${partner}/${token}${window.location.search}`);
    if (candidate?.user_id) {
      setUserId(candidate.user_id);
    }
    if (candidate?.team_id) {
      setTeamId(candidate.team_id);
    }
  }, [candidate]);

  // Get brand documents
  const handleGetBrandUploads = async brandId => {
    const documents = await getBrandUploads({ token, brandId }).unwrap();
    return { payload: { uploads: documents } };
  };

  // Errors redirect
  useEffect(() => {
    if (!candidateFetchError) return;

    switch (candidateFetchError.status) {
      case 404:
        navigate(`/not_found_error?message=Candidate Not Found`);
        break;
      case 500:
        navigate('/checkmate_error');
        break;
    }
  }, [candidateFetchError]);

  // Get current check based on type
  useEffect(() => {
    if (!candidate?.candidate_checks) return;
    setCheck(candidate.candidate_checks.find(check => check.type === checkType));
  }, [candidate?.candidate_checks?.length]);

  const notificationEmailError = JSON.parse(ccNotificationEmailsEnabled)
    ? patternValidators.emailList.validate(ccNotificationEmails)
    : true;

  const submit = async checksData => {
    if (notificationEmailError !== true) return;
    setLoading(true);

    // Update candidate
    let candidateData = checksData.candidate;
    candidateData.user_id = userId;
    if (teamId) candidateData.team_id = teamId;
    if (ccNotificationEmailsEnabled) {
      candidateData.cc_notification_emails = normalizeEmailList(ccNotificationEmails);
    }
    await updateCandidate({ token, applicationId: partnerApplicationId, candidateData });

    // Mark candidate as started for partners
    await startPartnerAssessment({ token, applicationId: partnerApplicationId });

    // Create checks
    await addCandidateChecks({
      token,
      applicationId: partnerApplicationId,
      checks: checksData.checks,
    });

    // Redirect to progress
    setLoading(false);
    navigate(`/assessments/progress/${partner}/${token}${window.location.search}`);
  };

  if (loadingForms || loadingCompanyChecks) return <Loading active={true} />;

  return (
    <div className={styles.root}>
      <Loading active={loading} />
      {company && (
        <div className={styles.wrapper}>
          <div className="px-6">
            <div
              className={cn(
                'border border-neutral-4 rounded-3xl shadow-sm p-8 bg-neutral-1 flex flex-col gap-8 w-full mb-4',
              )}>
              <div className="u-width-100">
                <div className="u-bold">User Assignment</div>
                <InputGroup
                  title="The user who is assigned to manage the candidate and receive notifications. This can be updated at any time."
                  className="u-margin-bottom--0">
                  <Select
                    name="user_id"
                    placeholder="User"
                    onChange={e => setUserId(e.target.value)}
                    value={userId}
                    useDefault
                    useSearch>
                    {(users || []).map(user => (
                      <Select.Item key={user.id} value={user.id}>
                        {user.first_name} {user.last_name} • {user.email}
                      </Select.Item>
                    ))}
                  </Select>
                </InputGroup>
              </div>
            </div>

            {teams?.length > 1 && (
              <div className="card card-with-border u-padding pb-6 mb-4">
                <InputGroup title="Team" className="u-margin-bottom--0">
                  <Select
                    name="team_id"
                    value={teamId}
                    onChange={e => setTeamId(e.target.value)}
                    placeholder="Select Team..."
                    useSearch>
                    {teams.map(team => (
                      <Select.Item key={team.id} value={team.id}>
                        {team.name}
                      </Select.Item>
                    ))}
                  </Select>
                </InputGroup>
              </div>
            )}

            <ToggleSwitchInput
              text="Notification List"
              subtext="Add external emails to get notified about status updates"
              onChange={e => setCcNotificationEmailsEnabled(e.target.checked)}
              value={JSON.parse(ccNotificationEmailsEnabled)}>
              <InputGroup title="Emails">
                <Input
                  placeholder="email_1@example.com,email_2@example.com"
                  error={notificationEmailError === true ? '' : notificationEmailError}
                  inputProps={{
                    value: ccNotificationEmails,
                    onChange: e => setCcNotificationEmails(e.target.value),
                  }}
                />
              </InputGroup>
            </ToggleSwitchInput>
          </div>
          <ChecksSelector
            ref={selectorRef}
            type={checkType}
            form={check?.details?.typeform_id}
            forms={forms}
            brands={brands}
            company={company}
            bundles={bundles}
            useDocuments
            getBrandUploads={handleGetBrandUploads}
            companyChecks={companyChecks}
            allowUploads
            setValidity={setValidity}
            onSubmit={submit}
            getUpload={getUpload}
            deleteUpload={deleteUpload}
            signUpload={signUpload}
            createUpload={createUpload}
          />
          <Button
            className="u-width-100 u-margin-top--large"
            disabled={!valid}
            large
            onClick={() => selectorRef.current.submit()}>
            Start
          </Button>
        </div>
      )}
    </div>
  );
};
